"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/cn";

interface BrandLetterNavItem {
  letter: string;
  targetId: string;
}

interface BrandLetterNavProps {
  items: BrandLetterNavItem[];
}

const STICKY_HEADER_OFFSET_PX = 104;
const SCROLL_DURATION_MS = 700;
const STICKY_OFFSET_EXTRA_PX = 12;
const TOP_LETTER_SCROLL_STEP_PX = 160;
const TOP_LETTER_SCROLL_THRESHOLD_PX = 2;

const TOP_LETTER_CHIP_BASE_CLASS =
  "inline-flex items-center justify-center border-0 px-2 py-1 text-[10px] leading-none font-normal transition-colors duration-150";

function easeInOutCubic(progress: number): number {
  if (progress < 0.5) {
    return 4 * progress * progress * progress;
  }
  return 1 - Math.pow(-2 * progress + 2, 3) / 2;
}

function animateScrollTo(
  top: number,
  durationMs: number,
  animationFrameRef: { current: number | null }
): void {
  if (animationFrameRef.current !== null) {
    window.cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = null;
  }

  const startY = window.scrollY;
  const delta = top - startY;

  if (Math.abs(delta) < 1) return;

  const startTime = performance.now();

  const step = (currentTime: number) => {
    const elapsedMs = currentTime - startTime;
    const progress = Math.min(elapsedMs / durationMs, 1);
    const easedProgress = easeInOutCubic(progress);
    window.scrollTo({
      top: startY + delta * easedProgress,
      left: 0,
    });

    if (progress < 1) {
      animationFrameRef.current = window.requestAnimationFrame(step);
      return;
    }

    animationFrameRef.current = null;
  };

  animationFrameRef.current = window.requestAnimationFrame(step);
}

function getLetterNavScrollOffset(letterNav: HTMLElement | null): number {
  if (!letterNav) return STICKY_HEADER_OFFSET_PX;

  const letterNavHeight = letterNav.getBoundingClientRect().height;
  const stickyTopPx = Number.parseFloat(window.getComputedStyle(letterNav).top || "0") || 0;

  if (letterNavHeight <= 0) return STICKY_HEADER_OFFSET_PX;
  return stickyTopPx + letterNavHeight;
}

export default function BrandLetterNav({ items }: BrandLetterNavProps) {
  const topLetterScrollRef = useRef<HTMLDivElement | null>(null);
  const letterNavRef = useRef<HTMLElement | null>(null);
  const scrollAnimationFrameRef = useRef<number | null>(null);
  const scrollFrameRef = useRef<number | null>(null);
  const firstTargetId = items[0]?.targetId ?? null;
  const [canScrollTopLettersLeft, setCanScrollTopLettersLeft] = useState(false);
  const [canScrollTopLettersRight, setCanScrollTopLettersRight] = useState(false);
  const [activeTargetId, setActiveTargetId] = useState<string | null>(firstTargetId);

  const cancelInFlightScroll = useCallback(() => {
    if (scrollAnimationFrameRef.current === null) return;
    window.cancelAnimationFrame(scrollAnimationFrameRef.current);
    scrollAnimationFrameRef.current = null;
  }, []);

  useEffect(() => {
    const handleUserScrollIntent = () => {
      cancelInFlightScroll();
    };

    window.addEventListener("wheel", handleUserScrollIntent, { passive: true });
    window.addEventListener("touchstart", handleUserScrollIntent, { passive: true });

    return () => {
      window.removeEventListener("wheel", handleUserScrollIntent);
      window.removeEventListener("touchstart", handleUserScrollIntent);
      cancelInFlightScroll();
    };
  }, [cancelInFlightScroll]);

  useEffect(() => {
    const evaluateScrollState = () => {
      const activationY =
        window.scrollY +
        getLetterNavScrollOffset(letterNavRef.current) +
        STICKY_OFFSET_EXTRA_PX * 2;
      let nextActiveTargetId = firstTargetId;

      for (const { targetId } of items) {
        const section = document.getElementById(targetId);
        if (!section) continue;
        if (section.offsetTop > activationY) break;
        nextActiveTargetId = targetId;
      }

      setActiveTargetId((previous) =>
        previous === nextActiveTargetId ? previous : nextActiveTargetId
      );
    };

    const handleScroll = () => {
      if (scrollFrameRef.current !== null) return;
      scrollFrameRef.current = window.requestAnimationFrame(() => {
        scrollFrameRef.current = null;
        evaluateScrollState();
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    evaluateScrollState();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }
    };
  }, [firstTargetId, items]);

  const scrollToTarget = useCallback(
    (targetId: string, animated: boolean) => {
      const target = document.getElementById(targetId);
      if (!target) return;

      const scrollOffset = getLetterNavScrollOffset(letterNavRef.current) + STICKY_OFFSET_EXTRA_PX;
      const targetTop = target.getBoundingClientRect().top + window.scrollY - scrollOffset;
      const safeTop = Math.max(0, targetTop);
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (animated && !prefersReducedMotion) {
        animateScrollTo(safeTop, SCROLL_DURATION_MS, scrollAnimationFrameRef);
      } else {
        cancelInFlightScroll();
        window.scrollTo({ top: safeTop, left: 0 });
      }

      setActiveTargetId(targetId);
      const targetHash = `#${targetId}`;
      if (window.location.hash !== targetHash) {
        window.history.replaceState(null, "", targetHash);
      }
    },
    [cancelInFlightScroll]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
      event.preventDefault();
      scrollToTarget(targetId, true);
    },
    [scrollToTarget]
  );

  const updateTopLetterScrollState = useCallback(() => {
    const scrollContainer = topLetterScrollRef.current;
    if (!scrollContainer) return;

    setCanScrollTopLettersLeft(scrollContainer.scrollLeft > TOP_LETTER_SCROLL_THRESHOLD_PX);
    setCanScrollTopLettersRight(
      scrollContainer.scrollLeft <
        scrollContainer.scrollWidth - scrollContainer.clientWidth - TOP_LETTER_SCROLL_THRESHOLD_PX
    );
  }, []);

  useEffect(() => {
    const scrollContainer = topLetterScrollRef.current;
    if (!scrollContainer) return;

    const animationFrameId = window.requestAnimationFrame(updateTopLetterScrollState);
    scrollContainer.addEventListener("scroll", updateTopLetterScrollState, { passive: true });
    window.addEventListener("resize", updateTopLetterScrollState, { passive: true });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      scrollContainer.removeEventListener("scroll", updateTopLetterScrollState);
      window.removeEventListener("resize", updateTopLetterScrollState);
    };
  }, [items.length, updateTopLetterScrollState]);

  const scrollTopLettersBy = useCallback((deltaPx: number) => {
    const scrollContainer = topLetterScrollRef.current;
    if (!scrollContainer) return;

    scrollContainer.scrollBy({ left: deltaPx, behavior: "smooth" });
  }, []);

  return (
    <nav
      ref={letterNavRef}
      aria-label="Brand letter navigation"
      className="sticky top-12 z-30 mb-8 border-y border-divider bg-white/95 py-3 backdrop-blur-sm lg:top-[74px]"
    >
      <div className="relative">
        {canScrollTopLettersLeft && (
          <button
            type="button"
            onClick={() => scrollTopLettersBy(-TOP_LETTER_SCROLL_STEP_PX)}
            className="absolute left-0 top-1/2 z-10 -translate-y-1/2 p-1 text-ink-muted hover:text-ink md:hidden"
            aria-label="Scroll letters left"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}

        <div
          ref={topLetterScrollRef}
          className="overflow-x-hidden pb-1 pr-5 md:overflow-visible md:px-0 md:pb-0"
        >
          <ul className="flex w-max flex-nowrap gap-2 md:w-auto md:flex-wrap md:gap-2">
            {items.map(({ letter, targetId }) => (
              <li key={letter}>
                <a
                  href={`#${targetId}`}
                  onClick={(event) => handleClick(event, targetId)}
                  aria-current={activeTargetId === targetId ? "location" : undefined}
                  className={cn(
                    TOP_LETTER_CHIP_BASE_CLASS,
                    activeTargetId === targetId
                      ? "rounded-none bg-transparent text-ink underline decoration-[1.5px] underline-offset-4"
                      : "rounded-none bg-transparent text-ink-muted hover:text-ink hover:underline hover:decoration-[1.5px] hover:underline-offset-4"
                  )}
                >
                  {letter}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {canScrollTopLettersRight && (
          <button
            type="button"
            onClick={() => scrollTopLettersBy(TOP_LETTER_SCROLL_STEP_PX)}
            className="absolute right-0 top-1/2 z-10 -translate-y-1/2 p-1 text-ink-muted hover:text-ink md:hidden"
            aria-label="Scroll letters right"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </nav>
  );
}
